/**
 * useNChat Hook
 * 
 * Manages N Agent chat state:
 * - Persistent WebSocket connection to /n/stream
 * - Message history and streaming
 * - Mission event injection (when missions complete, artifacts appear in chat)
 * 
 * Conversation is kept in-memory (React state) for now.
 * Multi-turn works within a session because the WebSocket stays open
 * and the backend maintains conversationHistory per connection.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWorkspaceFeatures } from '@/hooks/useWorkspaceFeatures';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

// ============================================
// TYPES
// ============================================

export interface ChatAttachment {
  url: string;
  mime_type: string;
  filename: string;
}

/** A grounded-answer citation (sovereign Grounded Chat). Mirrors the evidence-layer
 *  Citation shape from the worker; rendered as a source chip under the answer. */
export interface ChatCitation {
  citation_id: string;
  marker: string;
  evidence_id: string;
  doc_id: string;
  source_title: string;
  source_url: string | null;
  page_start?: number | null;
  page_end?: number | null;
  freshness?: string;
}

export interface PhaseStep {
  step: string;
  label: string;
  status: 'started' | 'in_progress' | 'completed' | 'failed';
  phase_name?: string;
  timestamp: number;
}

export interface PlanningAdvisorMessage {
  role: 'strategist' | 'challenger' | 'synthesizer';
  content: string;
  round: number;
  tool_calls?: Array<{ tool_name: string; result?: string }>;
}

/** Live activity feed item for an advisor (Mar 2026 — shows what each advisor is doing in real-time). */
export interface PlanningAdvisorActivity {
  role: string;
  activity: 'tool_call' | 'tool_result';
  tool_name: string;
  summary?: string;
  iteration?: number;
  timestamp: number;
}

/** Which advisor is currently active, for progress display. */
export interface PlanningAdvisorPhase {
  role: string;
  phase: 'started' | 'completed';
  advisor_index: number;   // 1-indexed
  total_advisors: number;  // always 3
}

export interface PlanningStep {
  title: string;
  description: string;
  phase_hint?: string;  // Maps to N71 phase for "Execute Step" action
  n71_method?: string;  // e.g. "Tier 2: execute_phase(presentation_builder)"
  timing?: string;      // "instant" | "minutes" | "background"
  depends_on?: number[]; // 1-indexed step dependencies
}

/** A clarifying question with clickable options (Cursor-style pre-planning). */
export interface PlanQuestion {
  id: string;        // "q1", "q2", "q3"
  question: string;
  options: string[];  // 2-4 clickable choices
}

export interface PlanningSession {
  status: 'questions' | 'investigating' | 'debating' | 'synthesizing' | 'complete';
  context_summary?: string;
  advisor_messages: PlanningAdvisorMessage[];
  /** Clarifying questions shown before planning starts (Cursor-style). */
  questions?: PlanQuestion[];
  /** Live activity feed — what each advisor is doing right now (Mar 2026). */
  advisor_activity?: PlanningAdvisorActivity[];
  /** Which advisor is currently active (Mar 2026). */
  current_advisor?: PlanningAdvisorPhase;
  plan?: {
    objective: string;
    approach: string;
    steps: PlanningStep[];
    risks: Array<{ risk: string; mitigation: string; severity: string } | string>;
    dependencies: string[];
    success_criteria?: string[];
    quick_start?: string;
  };
  /** Execution state — tracks progress when "Execute Plan" runs (Mar 2026). */
  execution?: {
    status: 'running' | 'completed' | 'partial' | 'failed';
    mission_id?: string;
    step_statuses?: Array<{ index: number; status: 'pending' | 'running' | 'completed' | 'failed'; detail?: string; duration_ms?: number; artifact_count?: number }>;
    completed_count?: number;
    failed_count?: number;
  };
}

export interface NChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  tool_calls?: Array<{ tool_name: string; result?: any }>;
  mission_launched?: { mission_id: string; mission_name: string };
  browser_status?: string;
  attachments?: ChatAttachment[];
  action_preview?: {
    preview_id: string;
    tool_name: string;
    tool_args: any;
    preview: any;
    status: 'pending' | 'approved' | 'rejected' | 'skipped' | 'failed';
    actioned_at?: string;   // ISO timestamp of when the user approved/skipped
    error?: string;         // Error message if the action failed after approval
  };
  phase_steps?: PhaseStep[];  // Inline phase activity stream from execute_phase
  /** Grounded-answer citations (sovereign Grounded Chat). Present on assistant turns
   *  answered via /n/conversational-stream; renders source chips under the answer. */
  citations?: ChatCitation[];
  /** True when the grounded answer refused (insufficient/unverifiable evidence). */
  grounded_refused?: boolean;
  integration_form?: {
    form_type: string;
    form_id: string;
    title: string;
    description?: string;
    draft?: any;
    required_fields?: string[];
    notes?: string[];
  };
  planning_session?: PlanningSession;  // Inline planning mode session
}

export interface NChatThread {
  id: string;
  title: string;
  status: 'idle' | 'running' | 'approval_required' | 'error' | 'completed';
  last_activity_at: string;
  last_message_preview?: string;
  unread_count?: number;
}

export type ChatMode = 'normal' | 'ask' | 'plan';

type ThreadModeState = {
  chatMode: ChatMode;
  deepResearchMode: boolean;
};

// Sovereign Grounded Chat — now a PER-WORKSPACE setting, not a build-time
// global. When workspaces.settings.feature_flags.strict_compliance_mode is
// true, that workspace's chat POSTs each turn to /n/conversational-stream
// (evidence-grounded, citation-validated, refuse-when-uncertain, NO tools).
// Default false → the workspace gets the full N-Agent over the /n/stream
// WebSocket (tools, conversation, citations). The old VITE_SOVEREIGN_GROUNDED_CHAT
// build flag is retired: it forced EVERY workspace on a sovereign build onto
// the tool-less grounded surface, which made the chat feel like a refusal
// machine. Read via useWorkspaceFeatures inside the hook (fails closed=false).

const DEFAULT_CHAT_MODE: ChatMode = 'ask';
const DEFAULT_THREAD_MODE_STATE: ThreadModeState = {
  chatMode: DEFAULT_CHAT_MODE,
  deepResearchMode: false,
};

const buildChatPayload = (
  threadId: string,
  message: string,
  imageUrls: string[] = [],
  modes: ThreadModeState = DEFAULT_THREAD_MODE_STATE,
  modelId?: string,
  surface?: string,
): Record<string, any> => ({
  type: 'chat',
  thread_id: threadId,
  message,
  image_urls: imageUrls,
  plan_mode: modes.chatMode === 'ask',
  planning_mode: modes.chatMode === 'plan',
  deep_research_mode: modes.deepResearchMode,
  model_id: modelId,
  // Only attach the surface marker when it's a non-default surface — keeps
  // the general-chat frame byte-identical to its previous shape.
  ...(surface && surface !== 'general' ? { surface } : {}),
});

/** Context attached to a chat message when user pins/references a mission or artifact. */
export interface ReferencedContext {
  mission_id: string;
  mission_name: string;
  /** If set, the user specifically wants to edit this artifact. */
  artifact_id?: string;
  artifact_type?: string;
  artifact_name?: string;
  /** 'edit' when the user clicked "Edit in Chat", 'reference' for generic mention, 'share_space' when creating a Space. */
  intent: 'edit' | 'reference' | 'share_space';
}

export type SendMessageResult = {
  accepted: boolean;
  mode: 'sent' | 'queued' | 'deferred' | 'suppressed';
  reason?: string;
};

export interface UseNChatReturn {
  messages: NChatMessage[];
  threads: NChatThread[];
  activeThreadId: string | null;
  chatMode: ChatMode;
  deepResearchMode: boolean;
  setChatMode: (mode: ChatMode) => void;
  setDeepResearchMode: (value: boolean) => void;
  isConnected: boolean;
  isStreaming: boolean;
  /** True from when sendMessage() is called until the first meaningful backend event arrives.
   *  Use this for a prominent "thinking" indicator — unlike isStreaming, it only covers
   *  the dead-air gap before N starts typing. */
  isThinking: boolean;
  sendMessage: (message: string, attachments?: File[], modelId?: string) => Promise<SendMessageResult>;
  sendActionResponse: (previewId: string, approved: boolean) => void;
  executePlan: (plan: PlanningSession) => void;
  /** Submit answers to plan-mode clarifying questions (click options flow). */
  submitPlanAnswers: (answers: Array<{ question: string; answer: string }>) => void;
  stopGeneration: () => void;
  /** Cursor-style force: user presses Enter on empty input while a message is queued.
   *  Stops current generation, then the queued message auto-sends on 'done'. */
  forceInterrupt: () => void;
  clearMessages: () => void;
  archiveChat: () => Promise<void>;
  archiveThread: (threadId?: string) => Promise<void>;
  createThread: () => Promise<void>;
  selectThread: (threadId: string) => Promise<void>;
  renameThread: (threadId: string, title: string) => Promise<void>;
  reorderThreads: (orderedThreadIds: string[]) => Promise<void>;
  isUploading: boolean;
  /** Currently pinned/referenced mission or artifact for the next message. */
  referencedContext: ReferencedContext | null;
  setReferencedContext: (ctx: ReferencedContext | null) => void;
  /** Cursor-style queued message — held client-side, shown as chip above input.
   *  null = nothing queued. Sent automatically when current task finishes. */
  queuedMessage: string | null;
  /** Start a fresh tab with summarized context from the current conversation (Mar 2026). */
  startFreshTab: () => void;
  /** True while the thread summary is being generated. */
  isSummarizing: boolean;
  /** Set the per-message surface marker ('general' | 'intelligence' | ...).
   *  Mounted surfaces (e.g. the Intelligence tab) call this to route the
   *  worker to the right system prompt + tool kit. Defaults to 'general'. */
  setSurface: (s: string) => void;
}

// ============================================
// HOOK
// ============================================

// ============================================
// PERSISTENCE (Supabase — n_chat_sessions/messages)
// ============================================

async function loadMessagesFromDB(
  workspaceId: string,
  userId: string,
  sessionId?: string | null,
): Promise<NChatMessage[]> {
  try {
    // FIX: sessionId is now always provided by the caller (from ensureSessionForThread).
    // The old "find any active session" fallback caused messages to load from a
    // DIFFERENT thread's session, making chats appear to swap contents.
    const resolvedSessionId = sessionId || null;
    if (!resolvedSessionId) return [];

    // Load messages
    // Include planning_session for plan card rehydration (Mar 2026)
    const { data: msgs } = await supabase
      .from('n_chat_messages')
      .select('id, role, content, tool_activity, mission_launched, planning_session, action_preview, citations, grounded_answer_metadata, created_at')
      .eq('session_id', resolvedSessionId)
      .order('created_at', { ascending: true })
      .limit(500);

    if (!msgs) return [];

    return (msgs as any[])
      .filter(m => (m.content && m.content.trim())
                 || (Array.isArray(m.tool_activity) && m.tool_activity.length > 0)
                 || m.mission_launched
                 || m.action_preview
                 || m.planning_session)
      .map(m => ({
        id: m.id,
        role: m.role,
        content: m.content || '',
        timestamp: new Date(m.created_at).getTime(),
        // tool_activity is the persisted shape of tool_calls (renamed
        // historically); rehydrate so brief / risk / decision cards
        // survive a refresh.
        tool_calls: Array.isArray(m.tool_activity) ? m.tool_activity : undefined,
        mission_launched: m.mission_launched || undefined,
        planning_session: m.planning_session || undefined,
        action_preview: m.action_preview || undefined,
        // Grounded-answer citations + refusal flag (sovereign Grounded Chat).
        citations: Array.isArray(m.citations) && m.citations.length > 0 ? m.citations : undefined,
        grounded_refused: m.grounded_answer_metadata?.refused || undefined,
      }));
  } catch (err) {
    console.warn('[useNChat] Failed to load history from DB:', err);
    return [];
  }
}

async function loadThreadsFromDB(workspaceId: string, userId: string): Promise<NChatThread[]> {
  const loadLatestPreviewForThread = async (threadId: string): Promise<string> => {
    try {
      const { data: session } = await supabase
        .from('n_chat_sessions')
        .select('id')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .eq('thread_id', threadId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!session?.id) return '';

      const { data: msg } = await supabase
        .from('n_chat_messages')
        .select('content')
        .eq('workspace_id', workspaceId)
        .eq('session_id', session.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return (msg?.content || '').trim().slice(0, 140);
    } catch {
      return '';
    }
  };

  const { data, error } = await supabase
    .from('n_chat_threads')
    .select('id, title, status, last_activity_at, last_message_preview, sort_order')
    .eq('workspace_id', workspaceId)
    .eq('owner_user_id', userId)
    .eq('is_archived', false)
    .order('is_pinned', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('last_activity_at', { ascending: false })
    .limit(20);

  if (error) {
    console.warn('[useNChat] Failed to load threads:', error.message);
    return [];
  }

  return Promise.all((data || []).map(async (t: any) => {
    const preview = t.last_message_preview || await loadLatestPreviewForThread(t.id);
    return {
      id: t.id,
      title: t.title || 'New chat',
      status: (t.status || 'idle') as NChatThread['status'],
      last_activity_at: t.last_activity_at || new Date().toISOString(),
      last_message_preview: preview || '',
      unread_count: 0,
    };
  }));
}

async function ensureSessionForThread(workspaceId: string, userId: string, threadId: string): Promise<string | null> {
  const { data: existing } = await supabase
    .from('n_chat_sessions')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .eq('thread_id', threadId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (existing?.id) return existing.id;

  const { data: created, error: createErr } = await supabase
    .from('n_chat_sessions')
    .insert({
      workspace_id: workspaceId,
      user_id: userId,
      thread_id: threadId,
      is_active: false,
    })
    .select('id')
    .single();
  if (createErr || !created?.id) {
    console.warn('[useNChat] Failed to create session for thread:', createErr?.message);
    return null;
  }
  return created.id;
}

// ============================================
// HOOK
// ============================================

export function useNChat(_planMode?: boolean, _deepResearchMode?: boolean): UseNChatReturn {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  // Per-workspace strict grounded mode (see the retired build-flag note above).
  // false while loading / for every unflagged workspace → the real N-Agent.
  const groundedChat = useWorkspaceFeatures(currentWorkspace?.id ?? null).features.strict_compliance_mode;
  // Per-thread message storage — messages are never lost on tab switch
  const threadMessagesRef = useRef<Map<string, NChatMessage[]>>(new Map());
  const [messages, setMessages] = useState<NChatMessage[]>([]);
  const [threads, setThreads] = useState<NChatThread[]>([]);
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [threadModes, setThreadModes] = useState<Record<string, ThreadModeState>>({});
  const threadModesRef = useRef(threadModes);
  threadModesRef.current = threadModes;
  const [isConnected, setIsConnected] = useState(false);
  // Per-thread streaming state — only the active thread's value is exposed as `isStreaming`
  const [streamingThreads, setStreamingThreads] = useState<Record<string, boolean>>({});
  const streamingThreadsRef = useRef(streamingThreads);
  streamingThreadsRef.current = streamingThreads;
  const isStreaming = activeThreadId ? (streamingThreads[activeThreadId] || false) : false;
  // Per-thread thinking state: true from sendMessage until first meaningful backend event.
  // Covers the dead-air gap before N starts typing — use for a prominent thinking indicator.
  const [thinkingThreads, setThinkingThreads] = useState<Record<string, boolean>>({});
  const isThinking = activeThreadId ? (thinkingThreads[activeThreadId] || false) : false;
  const [isUploading, setIsUploading] = useState(false);
  // Referenced context: mission/artifact that the user has pinned for the next message
  const [referencedContext, setReferencedContext] = useState<ReferencedContext | null>(null);
  // Ref mirror so sendMessage (memoized via useCallback) always reads the latest value.
  // Without this, referencedContext is captured as a stale closure and button clicks
  // like "Reference in Chat" / "Edit in Chat" silently fail to attach the mission_id.
  const referencedContextRef = useRef<ReferencedContext | null>(null);
  referencedContextRef.current = referencedContext;
  // Mar 2026: "Start Fresh" — summarizing state for the current thread
  const [isSummarizing, setIsSummarizing] = useState(false);

  // FEB 2026: Cursor-style client-side message queue — holds ONE message per thread.
  // Message stays here (shown as chip above input) until N finishes, then auto-sends.
  const [queuedMessages, setQueuedMessages] = useState<Record<string, { text: string; imageUrls?: string[]; attachments?: ChatAttachment[] }>>({});
  const queuedMessage = activeThreadId ? (queuedMessages[activeThreadId]?.text || null) : null;
  const queuedMessagesRef = useRef(queuedMessages);
  queuedMessagesRef.current = queuedMessages;
  // Holds a forced message (from forceInterrupt) that should be sent on the next 'done' event.
  // Separate from queuedMessages because the UI transition already happened optimistically.
  const forcedMessageRef = useRef<{ threadId: string; queued: { text: string; imageUrls?: string[]; attachments?: ChatAttachment[] } } | null>(null);
  
  const socketRef = useRef<WebSocket | null>(null);
  // Per-message surface signal. The NChat WebSocket is a single app-level
  // socket shared by the general chat AND the Intelligence tab, so the
  // surface MUST ride on each chat frame — it cannot be a connection param.
  // 'general' (default) leaves the worker behavior unchanged; 'intelligence'
  // routes the worker to the read-only consciousness prompt + tool kit.
  const surfaceRef = useRef<string>('general');
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks when thinking started per thread so we can enforce a minimum visible duration.
  // Without this, fast backend responses (~100-300ms on follow-ups) flash the indicator
  // too briefly for the user to see.
  const thinkingStartRef = useRef<Record<string, number>>({});
  // Guard against concurrent async connect() calls — the await on getSession() creates a
  // window where React re-renders can trigger a second connect() before the first completes.
  // Without this, two live WebSocket connections receive the same server messages → double responses.
  const connectingRef = useRef(false);
  // Tag the in-flight connect with its target workspace id so a second
  // connect call for a DIFFERENT workspace (the workspace-switch race)
  // can proceed instead of being suppressed as a duplicate.
  const connectingForWorkspaceRef = useRef<string | null>(null);
  // Per-thread assistant accumulator
  type AssistantAccum = { id: string; content: string; tool_calls: Array<{ tool_name: string; result?: any }>; mission_launched?: { mission_id: string; mission_name: string }; browser_status?: string; phase_steps?: PhaseStep[]; planning_session?: PlanningSession };
  const assistantsByThread = useRef<Map<string, AssistantAccum>>(new Map());
  // Legacy single ref — points to the active thread's accumulator for backward compat in rendering
  const currentAssistantRef = useRef<AssistantAccum | null>(null);
  const pendingMessagesRef = useRef<Array<{ message: string; threadId: string; chatMode: ChatMode; deepResearchMode: boolean; imageUrls?: string[]; _plan?: any; _isPlanExecution?: boolean }>>([]);
  
  const workerUrl = import.meta.env.VITE_WORKER_URL || 'https://n71-mission-worker.fly.dev';
  // Mohap chat uses a dedicated Fly worker (n71-mohap-worker) so the
  // Minister's pipeline is never starved by sync engine, backfills,
  // or any other heavy workload running on n71-mission-worker. Detection
  // is by URL prefix to mirror the existing _isMohapEdition pattern in
  // NChat.tsx; non-Mohap users keep hitting workerUrl unchanged.
  const _isMohapPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/mohap');
  // Elias edition — same isolation rationale as Mohap. Routes WebSocket
  // chat to n71-elias-worker so Elias's pipeline never shares runtime
  // memory with the public N71 worker (privacy posture) and is never
  // starved by sync engine, backfills, or Mohap traffic.
  const _isEliasPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/elias');
  const wsWorkerUrl = _isMohapPath
    ? (import.meta.env.VITE_MOHAP_WORKER_URL || 'https://n71-mohap-worker.fly.dev')
    : _isEliasPath
      ? (import.meta.env.VITE_ELIAS_WORKER_URL || 'https://n71-elias-worker.fly.dev')
      : workerUrl;
  const activeModes = activeThreadId
    ? (threadModes[activeThreadId] || DEFAULT_THREAD_MODE_STATE)
    : DEFAULT_THREAD_MODE_STATE;
  // Ref mirror of activeModes so connect() can read current values without re-creating the callback.
  // This prevents WebSocket reconnection (and message replay) when toggling plan/research mode.
  const activeModesRef = useRef(activeModes);
  activeModesRef.current = activeModes;
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;

  // Update messages for a specific thread. If it's the active thread, also update React state.
  const setThreadMessages = useCallback((threadId: string, updater: (prev: NChatMessage[]) => NChatMessage[]) => {
    const prev = threadMessagesRef.current.get(threadId) || [];
    const next = updater(prev);
    threadMessagesRef.current.set(threadId, next);
    // Only push to React state if this is the currently visible thread
    if (threadId === activeThreadIdRef.current) {
      setMessages(next);
    }
  }, []);

  const isGenericThreadTitle = (title?: string) => {
    const t = (title || '').trim().toLowerCase();
    return !t
      || t === 'new chat'
      || t === 'conversation'
      || t === 'untitled conversation'
      || t.startsWith('tab ');
  };

  const updateThread = useCallback((threadId: string, patch: Partial<NChatThread>) => {
    setThreads(prev => prev.map(t => t.id === threadId ? { ...t, ...patch } : t));
    if (!currentWorkspace?.id || !user?.id) return;

    // Only send columns that actually exist on n_chat_threads.
    // Sending unknown columns (e.g. unread_count) causes silent Supabase update failures
    // which is why title, status, and last_message_preview were not persisting.
    const dbPayload: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };
    if ('title' in patch && patch.title !== undefined) dbPayload.title = patch.title;
    if ('status' in patch && patch.status !== undefined) dbPayload.status = patch.status;
    if ('last_activity_at' in patch && patch.last_activity_at !== undefined) dbPayload.last_activity_at = patch.last_activity_at;
    if ('last_message_preview' in patch && patch.last_message_preview !== undefined) dbPayload.last_message_preview = patch.last_message_preview;

    void supabase
      .from('n_chat_threads')
      .update(dbPayload)
      .eq('id', threadId)
      .eq('workspace_id', currentWorkspace.id)
      .eq('owner_user_id', user.id);
  }, [currentWorkspace?.id, user?.id]);

  const setChatMode = useCallback((mode: ChatMode) => {
    if (!activeThreadId) return;
    setThreadModes(prev => ({
      ...prev,
      [activeThreadId]: {
        chatMode: mode,
        deepResearchMode: prev[activeThreadId]?.deepResearchMode ?? false,
      },
    }));
  }, [activeThreadId]);

  const setDeepResearchMode = useCallback((value: boolean) => {
    if (!activeThreadId) return;
    setThreadModes(prev => ({
      ...prev,
      [activeThreadId]: {
        chatMode: prev[activeThreadId]?.chatMode ?? DEFAULT_CHAT_MODE,
        deepResearchMode: value,
      },
    }));
  }, [activeThreadId]);

  const persistThreadOrder = useCallback(async (orderedThreadIds: string[]) => {
    if (!currentWorkspace?.id || !user?.id || orderedThreadIds.length === 0) return;
    await Promise.all(
      orderedThreadIds.map((id, idx) =>
        supabase
          .from('n_chat_threads')
          .update({ sort_order: idx, updated_at: new Date().toISOString() })
          .eq('id', id)
          .eq('workspace_id', currentWorkspace.id)
          .eq('owner_user_id', user.id)
      )
    );
  }, [currentWorkspace?.id, user?.id]);

  // Reload messages from DB — called on mount AND on thread switch to sync state.
  // Uses refs for activeSessionId/activeThreadId so this callback is STABLE and
  // does NOT cause WebSocket reconnections when the active thread changes.
  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;
  const updateThreadRef = useRef(updateThread);
  updateThreadRef.current = updateThread;

  const reloadMessagesFromDB = useCallback(async () => {
    if (!currentWorkspace?.id || !user?.id) return;
    try {
      const sessionId = activeSessionIdRef.current;
      const threadId = activeThreadIdRef.current;
      const dbMessages = await loadMessagesFromDB(currentWorkspace.id, user.id, sessionId);
      // Merge DB messages with in-flight (optimistic/streaming) messages.
      // CRITICAL: Never wipe to [] here — in-flight messages (streaming responses, optimistic
      // user messages) only exist in-memory and would be permanently lost. The workspace-switch
      // cleanup in the init effect handles the "clear everything" case; this function only ADDS data.
      const mergeWithInFlight = (prev: NChatMessage[]) => {
        if (dbMessages.length === 0) return prev; // No DB data → keep whatever is in-memory

        const dbIds = new Set(dbMessages.map(m => m.id));
        // Build content sets for deduplication of optimistic messages that have been persisted to DB.
        // User messages: match by trimmed content
        const dbUserContent = new Set(
          dbMessages.filter(m => m.role === 'user').map(m => m.content?.trim()).filter(Boolean)
        );
        // Assistant messages: match by trimmed content (first 200 chars to handle long messages)
        // This prevents the same assistant response appearing twice when DB reload fires
        // while an in-flight n-{ts} message matches a persisted UUID message.
        const dbAssistantContent = new Set(
          dbMessages.filter(m => m.role === 'assistant' && m.content?.trim()).map(m => m.content!.trim().slice(0, 200))
        );
        // Check if DB already has a planning session message (for dedup of in-flight plan cards)
        const dbHasPlanningSession = dbMessages.some(m => m.planning_session);
        const inFlightMessages = prev.filter(m => {
          if (dbIds.has(m.id)) return false; // Already in DB by ID
          if (!(m.id.startsWith('u-') || m.id.startsWith('n-') || m.id.startsWith('preview-'))) return false;
          // Drop optimistic user messages that already made it to DB (content match)
          if (m.id.startsWith('u-') && m.role === 'user' && dbUserContent.has(m.content?.trim())) return false;
          // Drop in-flight assistant messages that already made it to DB (content match)
          if (m.id.startsWith('n-') && m.role === 'assistant' && m.content?.trim() && dbAssistantContent.has(m.content.trim().slice(0, 200))) return false;
          // Drop in-flight planning session cards when DB already has the persisted version (Mar 2026).
          // The in-flight card has empty content (renders from planning_session), so the content
          // dedup above doesn't catch it — this explicit check prevents duplicate plan cards.
          if (m.id.startsWith('n-') && m.planning_session && dbHasPlanningSession) return false;
          // Drop approval cards that have already been acted on (approved/rejected)
          if (m.id.startsWith('preview-') && m.action_preview && m.action_preview.status !== 'pending') return false;
          return true;
        });
        // Sort merged messages by timestamp so in-flight messages appear in the right
        // position, not always at the end (Mar 2026 — fixes plan card appearing after user msg on tab switch).
        const merged = [...dbMessages, ...inFlightMessages];
        merged.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        return merged;
      };

      // Store in per-thread ref
      if (threadId) {
        const prev = threadMessagesRef.current.get(threadId) || [];
        threadMessagesRef.current.set(threadId, mergeWithInFlight(prev));
      }

      setMessages(prev => mergeWithInFlight(prev));

      // Backfill preview for threads created before preview tracking existed.
      if (threadId && dbMessages.length > 0) {
        const latestContent = [...dbMessages]
          .reverse()
          .find((m) => (m.content || '').trim())
          ?.content
          ?.slice(0, 140);
        if (latestContent) {
          updateThreadRef.current(threadId, {
            last_message_preview: latestContent,
            last_activity_at: new Date().toISOString(),
          });
        }
      }
    } catch (err) {
      console.warn('[useNChat] Failed to reload messages:', err);
    }
  }, [currentWorkspace?.id, user?.id]);

  // Stable refs so connect() doesn't depend on reloadMessagesFromDB/updateThread identity
  const reloadMessagesFromDBRef = useRef(reloadMessagesFromDB);
  reloadMessagesFromDBRef.current = reloadMessagesFromDB;

  // Connect WebSocket
  const connect = useCallback(async () => {
    if (!currentWorkspace?.id || !user?.id) {
      console.log('[useNChat] connect: skip — missing', !currentWorkspace?.id ? 'workspace' : 'user');
      return;
    }

    // Capture the intended workspace at the START. We re-check after every
    // await so the connect completes for the workspace that was current
    // when the user wanted to connect — not whichever workspace happened
    // to be in the closure. Stops the "workspace switched mid-connect"
    // race that pinned the chat WS to the wrong workspace forever.
    const intendedWorkspaceId = currentWorkspace.id;

    // Workspace-mismatch guard: if there's an open socket for a DIFFERENT
    // workspace (the user just switched workspaces — this is the /mohap
    // → MoHAP case where the page initially loaded under the user's
    // personal workspace and MinistryShell then switched), close it
    // before opening a new one. Without this, the existing OPEN socket
    // passes the readyState check below and the chat keeps talking to
    // the wrong workspace's agent (with the wrong tool kit).
    const expectedWsParam = `workspace_id=${encodeURIComponent(intendedWorkspaceId)}`;
    if (socketRef.current && !socketRef.current.url.includes(expectedWsParam)) {
      console.log('[useNChat] workspace changed — closing socket for stale workspace');
      try { socketRef.current.close(1000, 'workspace changed'); } catch { /* noop */ }
      socketRef.current = null;
      setIsConnected(false);
    }

    // Don't reconnect if already open or connecting FOR THE INTENDED WORKSPACE.
    if (socketRef.current?.readyState === WebSocket.OPEN || socketRef.current?.readyState === WebSocket.CONNECTING) {
      console.log(`[useNChat] connect: skip — socket already ${socketRef.current.readyState === WebSocket.OPEN ? 'OPEN' : 'CONNECTING'} for workspace=${intendedWorkspaceId}`);
      return;
    }

    // Prevent concurrent async connect() calls FOR THE SAME WORKSPACE.
    // connect() awaits getSession() below, creating a window where React
    // re-renders can trigger a second call. We tag the in-flight connect
    // with the workspace id it's connecting for, so a workspace switch
    // while a connect is in flight is recognised as DIFFERENT and a
    // second connect for the new workspace can proceed (the old one
    // self-cancels via the post-await currentWorkspace mismatch check).
    if (connectingRef.current && connectingForWorkspaceRef.current === intendedWorkspaceId) {
      console.log('[useNChat] Connect already in progress for this workspace, skipping');
      return;
    }
    connectingRef.current = true;
    connectingForWorkspaceRef.current = intendedWorkspaceId;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn('[useNChat] No session token available');
        connectingRef.current = false; connectingForWorkspaceRef.current = null;
        return;
      }

      // Post-await: re-evaluate. Three cases for an existing socket:
      //   (a) opened during our await for the SAME workspace → bail (duplicate).
      //   (b) opened during our await for a DIFFERENT workspace → close it and
      //       proceed; the older connect won the race for the wrong workspace
      //       (this is the /mohap landing race — Sanad's Context connect ran
      //       first, MoHAP connect was queued, and Sanad's WS opened mid-await).
      //   (c) no socket open → proceed.
      if (socketRef.current?.readyState === WebSocket.OPEN || socketRef.current?.readyState === WebSocket.CONNECTING) {
        if (socketRef.current.url.includes(`workspace_id=${encodeURIComponent(intendedWorkspaceId)}`)) {
          console.log('[useNChat] Socket opened by another call while awaiting token (same workspace)');
          connectingRef.current = false; connectingForWorkspaceRef.current = null;
          return;
        }
        console.log('[useNChat] Socket opened by another call but for stale workspace — closing');
        try { socketRef.current.close(1000, 'workspace changed'); } catch { /* noop */ }
        socketRef.current = null;
        setIsConnected(false);
      }

      // If the WorkspaceContext switched again WHILE we were awaiting the
      // session token, our captured intendedWorkspaceId is now stale. Bail
      // and let the latest effect run a fresh connect.
      if (currentWorkspace?.id !== intendedWorkspaceId) {
        console.log(`[useNChat] workspace changed mid-connect (${intendedWorkspaceId} -> ${currentWorkspace?.id}); bailing`);
        connectingRef.current = false; connectingForWorkspaceRef.current = null;
        return;
      }

      // Build WebSocket URL — no session_id; threads are multiplexed over one connection.
      // Use wsWorkerUrl so Mohap chat connects to n71-mohap-worker (dedicated
      // Fly app); non-Mohap chats stay on n71-mission-worker.
      const wsBase = wsWorkerUrl.replace(/^http/, 'ws');
      const url = `${wsBase}/n/stream`
        + `?token=${encodeURIComponent(session.access_token)}`
        + `&workspace_id=${encodeURIComponent(currentWorkspace.id)}`;

      console.log('[useNChat] Connecting to N Agent...');
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('[useNChat] WebSocket connected');
        connectingRef.current = false; connectingForWorkspaceRef.current = null;
        setIsConnected(true);
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        // Reload messages from DB on reconnect (via ref to avoid dep chain)
        reloadMessagesFromDBRef.current();

        // Restore the thinking indicator for any agent runs that were
        // still in-flight when the user closed the tab / navigated away.
        // agent_runs is workspace-scoped + owner-readable (RLS policy
        // agent_runs_owner_select). If a run is status='running' for the
        // current user + workspace, surface its thread as "thinking" so
        // the user sees the same indicator they had before they left.
        // The actual completion will land via the normal n_chat_messages
        // reload path when the worker finishes the run.
        (async () => {
          try {
            // Only restore runs newer than 10 minutes. Older rows stuck at
            // status='running' are almost always crashed runs (the worker
            // failed to write complete_run before dying); treating them as
            // active locks the user out of the thread because sendMessage
            // queues new messages client-side when streamingThreads[t]=true.
            const recentCutoff = new Date(Date.now() - 10 * 60_000).toISOString();
            const { data: runs } = await supabase
              .from('agent_runs')
              .select('run_id, thread_id, started_at, user_message')
              .eq('workspace_id', currentWorkspace.id)
              .eq('status', 'running')
              .gt('started_at', recentCutoff)
              .order('started_at', { ascending: false })
              .limit(20);
            if (!runs || runs.length === 0) return;

            const restored = runs as Array<{ run_id: string; thread_id: string; started_at: string; user_message: string | null }>;
            for (const r of restored) {
              if (!r.thread_id) continue;
              thinkingStartRef.current[r.thread_id] = new Date(r.started_at).getTime();
              setThinkingThreads(prev => ({ ...prev, [r.thread_id]: true }));
              setStreamingThreads(prev => ({ ...prev, [r.thread_id]: true }));
            }
            console.log(`[useNChat] Restored ${restored.length} in-flight agent run(s) on reconnect`);

            // Build a synthetic assistant message from agent_run_events
            // that have already been persisted, so the user immediately
            // sees the partial trace (tool calls + their results + any
            // streamed text) instead of an empty "thinking" placeholder.
            // We also fold in the user message that kicked the run off so
            // the conversation flow reads correctly.
            const synthesizeFromEvents = (
              events: Array<{ seq: number; event_type: string; payload: Record<string, unknown> }>,
            ) => {
              let content = '';
              const toolCalls: Array<{ tool_name: string; result?: unknown }> = [];
              for (const e of events) {
                const p = (e.payload ?? {}) as Record<string, unknown>;
                if (e.event_type === 'text_delta' && typeof p.content === 'string') {
                  content += p.content as string;
                } else if (e.event_type === 'tool_call' && typeof p.tool_name === 'string') {
                  toolCalls.push({ tool_name: p.tool_name as string });
                } else if (e.event_type === 'tool_result' && typeof p.tool_name === 'string') {
                  // attach to the most recent tool_call of the same name
                  // that doesn't yet have a result
                  for (let i = toolCalls.length - 1; i >= 0; i--) {
                    if (toolCalls[i].tool_name === (p.tool_name as string) && toolCalls[i].result === undefined) {
                      toolCalls[i].result = p.tool_result;
                      break;
                    }
                  }
                }
              }
              return { content, toolCalls };
            };

            const lastSeqByRun = new Map<string, number>();

            for (const r of restored) {
              if (!r.thread_id) continue;

              const { data: events } = await supabase
                .from('agent_run_events')
                .select('seq, event_type, payload')
                .eq('run_id', r.run_id)
                .order('seq', { ascending: true })
                .limit(500);

              const evList = (events ?? []) as Array<{ seq: number; event_type: string; payload: Record<string, unknown> }>;
              if (evList.length === 0) {
                lastSeqByRun.set(r.run_id, -1);
                continue;
              }
              lastSeqByRun.set(r.run_id, evList[evList.length - 1].seq);

              const { content, toolCalls } = synthesizeFromEvents(evList);

              const stableUserId = `replay-u-${r.run_id}`;
              const stableAssistantId = `replay-a-${r.run_id}`;

              setThreadMessages(r.thread_id, prev => {
                // If we already injected a replay shell for this run,
                // update in place; otherwise insert user + assistant.
                const existingIdx = prev.findIndex(m => m.id === stableAssistantId);
                if (existingIdx >= 0) {
                  const next = [...prev];
                  next[existingIdx] = {
                    ...next[existingIdx],
                    content,
                    tool_calls: toolCalls.length > 0 ? toolCalls : next[existingIdx].tool_calls,
                  };
                  return next;
                }
                const additions: NChatMessage[] = [];
                if (r.user_message && !prev.some(m => m.id === stableUserId)) {
                  additions.push({
                    id: stableUserId,
                    role: 'user',
                    content: r.user_message,
                    timestamp: new Date(r.started_at).getTime(),
                  });
                }
                additions.push({
                  id: stableAssistantId,
                  role: 'assistant',
                  content,
                  timestamp: new Date(r.started_at).getTime() + 1,
                  tool_calls: toolCalls.length > 0 ? toolCalls : undefined,
                });
                return [...prev, ...additions];
              });
            }

            // Watch loop: poll status + diff new events every 3s. Stops
            // when the run leaves 'running' or 5 min elapses.
            const watchUntilDone = async (runId: string, threadId: string) => {
              const deadline = Date.now() + 5 * 60_000;
              while (Date.now() < deadline) {
                await new Promise((res) => setTimeout(res, 3000));

                const lastSeq = lastSeqByRun.get(runId) ?? -1;
                const { data: newEvents } = await supabase
                  .from('agent_run_events')
                  .select('seq, event_type, payload')
                  .eq('run_id', runId)
                  .gt('seq', lastSeq)
                  .order('seq', { ascending: true })
                  .limit(500);

                const newList = (newEvents ?? []) as Array<{ seq: number; event_type: string; payload: Record<string, unknown> }>;
                if (newList.length > 0) {
                  lastSeqByRun.set(runId, newList[newList.length - 1].seq);
                  const stableAssistantId = `replay-a-${runId}`;
                  setThreadMessages(threadId, prev => {
                    const idx = prev.findIndex(m => m.id === stableAssistantId);
                    if (idx < 0) return prev;
                    const msg = prev[idx];
                    let content = msg.content;
                    const toolCalls: Array<{ tool_name: string; result?: unknown }> =
                      Array.isArray(msg.tool_calls) ? [...msg.tool_calls] : [];
                    for (const e of newList) {
                      const p = (e.payload ?? {}) as Record<string, unknown>;
                      if (e.event_type === 'text_delta' && typeof p.content === 'string') {
                        content += p.content as string;
                      } else if (e.event_type === 'tool_call' && typeof p.tool_name === 'string') {
                        toolCalls.push({ tool_name: p.tool_name as string });
                      } else if (e.event_type === 'tool_result' && typeof p.tool_name === 'string') {
                        for (let i = toolCalls.length - 1; i >= 0; i--) {
                          if (toolCalls[i].tool_name === (p.tool_name as string) && toolCalls[i].result === undefined) {
                            toolCalls[i].result = p.tool_result;
                            break;
                          }
                        }
                      }
                    }
                    const next = [...prev];
                    next[idx] = { ...msg, content, tool_calls: toolCalls };
                    return next;
                  });
                }

                const { data: row } = await supabase
                  .from('agent_runs')
                  .select('status')
                  .eq('run_id', runId)
                  .maybeSingle();
                if (!row || row.status !== 'running') {
                  setThinkingThreads(prev => ({ ...prev, [threadId]: false }));
                  setStreamingThreads(prev => ({ ...prev, [threadId]: false }));
                  // Reload so the persisted final assistant message
                  // (with the canonical id) replaces our replay shell.
                  reloadMessagesFromDBRef.current();
                  return;
                }
              }
              // Deadline hit — clear stuck indicator defensively.
              setThinkingThreads(prev => ({ ...prev, [threadId]: false }));
              setStreamingThreads(prev => ({ ...prev, [threadId]: false }));
            };
            for (const r of restored) {
              if (r.thread_id) void watchUntilDone(r.run_id, r.thread_id);
            }
          } catch (err) {
            console.warn('[useNChat] Failed to restore active runs:', err);
          }
        })();

        // Flush queued messages that were sent while socket was disconnected
        if (pendingMessagesRef.current.length > 0 && socket.readyState === WebSocket.OPEN) {
          const queued = [...pendingMessagesRef.current];
          pendingMessagesRef.current = [];
          for (const pending of queued) {
            // FEB 2026: Handle plan execution messages differently from chat messages
            if (pending._isPlanExecution && pending._plan) {
              console.log(`[useNChat] Sending queued plan execution for thread ${pending.threadId}`);
              socket.send(JSON.stringify({
                type: 'execute_plan',
                thread_id: pending.threadId,
                plan: pending._plan,
                message: pending.message,
              }));
            } else {
              console.log(`[useNChat] Sending queued message for thread ${pending.threadId}`);
              socket.send(JSON.stringify(buildChatPayload(
                pending.threadId,
                pending.message,
                pending.imageUrls || [],
                {
                  chatMode: pending.chatMode,
                  deepResearchMode: pending.deepResearchMode,
                },
                undefined,
                surfaceRef.current,
              )));
            }
            setStreamingThreads(prev => ({ ...prev, [pending.threadId]: true }));
            thinkingStartRef.current[pending.threadId] = Date.now();
            setThinkingThreads(prev => ({ ...prev, [pending.threadId]: true }));
          }
        }
        
        // Auto-briefing removed (Mar 2026) — felt glitchy, wasted credits,
        // and made the product feel like it had a mind of its own.
        // Users who want a briefing can just say "brief me".
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          // Route by thread_id — multiplexed protocol
          const msgThreadId = msg.thread_id || activeThreadId || 'default';

          // Clear "thinking" state when first meaningful content arrives.
          // Enforce a minimum visible duration (800ms) so the indicator doesn't flash
          // invisibly on fast follow-up responses where the backend responds in ~100-300ms.
          const CONTENT_EVENTS = new Set(['text_delta', 'tool_call', 'tool_result', 'mission_launched', 'step', 'action_preview', 'planning_questions', 'planning_status', 'planning_advisor', 'planning_plan', 'planning_advisor_activity', 'planning_advisor_phase', 'planning_checkpoint', 'done', 'error']);
          if (CONTENT_EVENTS.has(msg.type)) {
            const clearThinking = () => {
              setThinkingThreads(prev => {
                if (!prev[msgThreadId]) return prev; // already cleared — skip rerender
                return { ...prev, [msgThreadId]: false };
              });
            };
            const startedAt = thinkingStartRef.current[msgThreadId] || 0;
            const elapsed = Date.now() - startedAt;
            const MIN_THINKING_MS = 800;
            if (elapsed >= MIN_THINKING_MS) {
              clearThinking();
            } else {
              setTimeout(clearThinking, MIN_THINKING_MS - elapsed);
            }
          }

          // Helper: get or create assistant accumulator for this thread
          const getAssistant = (): AssistantAccum => {
            let acc = assistantsByThread.current.get(msgThreadId);
            if (!acc) {
              const newId = `n-${Date.now()}-${msgThreadId.slice(0, 8)}`;
              acc = { id: newId, content: '', tool_calls: [] };
              assistantsByThread.current.set(msgThreadId, acc);
              // Add to this thread's message store (works for both active and background threads)
              setThreadMessages(msgThreadId, prev => [...prev, {
                id: newId,
                role: 'assistant',
                content: '',
                timestamp: Date.now(),
              }]);
            }
            // Keep legacy ref in sync for active thread
            if (msgThreadId === activeThreadIdRef.current) {
              currentAssistantRef.current = acc;
            }
            return acc;
          };

          // Update messages for this thread (active or background — both are stored)
          const updateThreadMessage = (acc: AssistantAccum, patch: Partial<NChatMessage>) => {
            setThreadMessages(msgThreadId, prev => prev.map(m => m.id === acc.id ? { ...m, ...patch } : m));
          };

          switch (msg.type) {
            case 'connected':
            case 'heartbeat':
            case 'thread_ready':
              break;
            
            // Server-side queue fallback — if a message somehow reaches the server while busy,
            // it gets queued there. We don't need to track this on the frontend since we now
            // hold messages client-side. Just acknowledge it silently.
            case 'message_queued':
              break;
            
            case 'text_delta': {
              updateThreadRef.current(msgThreadId, {
                status: 'running',
                last_activity_at: new Date().toISOString(),
              });
              const acc = getAssistant();
              if (msg.data?.content) {
                acc.content += msg.data.content;
                updateThreadMessage(acc, { content: acc.content });
              }
              break;
            }
            
            case 'tool_call': {
              const acc = getAssistant();
              if (msg.data?.tool_name) {
                acc.tool_calls.push({ tool_name: msg.data.tool_name });
                updateThreadMessage(acc, { tool_calls: [...acc.tool_calls] });
              }
              break;
            }
            
            case 'tool_result': {
              const acc = assistantsByThread.current.get(msgThreadId);
              if (acc && msg.data?.tool_name) {
                const tc = acc.tool_calls.find(t => t.tool_name === msg.data.tool_name && !t.result);
                if (tc) {
                  // Worker emits the result under msg.data.tool_result; some
                  // older paths under msg.data.result. Take whichever exists.
                  tc.result = msg.data.tool_result ?? msg.data.result;
                  const toolResultData = msg.data?.tool_result?.data || msg.data?.result?.data;
                  const isIntegrationForm = toolResultData?.form_type === 'custom_integration';
                  updateThreadMessage(acc, {
                    tool_calls: [...acc.tool_calls],
                    ...(isIntegrationForm ? { integration_form: toolResultData } : {}),
                  });
                }
              }
              break;
            }
            
            case 'browser_status': {
              const acc = assistantsByThread.current.get(msgThreadId);
              if (acc) {
                acc.browser_status = msg.data?.status;
                updateThreadMessage(acc, { browser_status: acc.browser_status });
              }
              break;
            }
            
            case 'mission_launched': {
              const acc = assistantsByThread.current.get(msgThreadId);
              if (acc && msg.data) {
                acc.mission_launched = { mission_id: msg.data.mission_id, mission_name: msg.data.mission_name };
                updateThreadMessage(acc, { mission_launched: acc.mission_launched });
              }
              break;
            }
            
            case 'step': {
              const acc = assistantsByThread.current.get(msgThreadId);
              if (acc && msg.data) {
                const stepId = msg.data.step || 'phase_step';

                // Mar 2026: Route plan execution steps back to the planning_session card.
                if (stepId.startsWith('plan_step_') || stepId === 'plan_checklist') {
                  if (acc.planning_session) {
                    if (!acc.planning_session.execution) {
                      acc.planning_session.execution = { status: 'running', step_statuses: [] };
                    }
                    const exec = acc.planning_session.execution;
                    if (stepId === 'plan_checklist') {
                      if (msg.data.data?.type === 'plan_execution_complete') {
                        exec.status = msg.data.status === 'completed' ? 'completed' : msg.data.status === 'failed' ? 'failed' : 'partial';
                        exec.completed_count = msg.data.data.completedCount;
                        exec.failed_count = msg.data.data.failedCount;
                      }
                    } else {
                      const stepNum = parseInt(stepId.replace('plan_step_', ''), 10);
                      if (!isNaN(stepNum)) {
                        if (!exec.step_statuses) exec.step_statuses = [];
                        const existing = exec.step_statuses.find(s => s.index === stepNum);
                        const ss = {
                          index: stepNum,
                          status: (msg.data.status === 'completed' ? 'completed' : msg.data.status === 'failed' ? 'failed' : 'running') as 'pending' | 'running' | 'completed' | 'failed',
                          detail: msg.data.detail,
                          duration_ms: msg.data.durationMs,
                          artifact_count: msg.data.artifactCount,
                        };
                        if (existing) Object.assign(existing, ss);
                        else exec.step_statuses.push(ss);
                      }
                    }
                    updateThreadMessage(acc, {
                      planning_session: { ...acc.planning_session, execution: { ...exec, step_statuses: [...(exec.step_statuses || [])] } },
                    });
                  }
                }

                const step: PhaseStep = {
                  step: stepId,
                  label: msg.data.label || '',
                  status: msg.data.status || 'in_progress',
                  phase_name: msg.data.phase_name,
                  timestamp: msg.data.timestamp || Date.now(),
                };
                if (!acc.phase_steps) acc.phase_steps = [];
                const existingIdx = acc.phase_steps.findIndex(s => s.step === step.step);
                if (existingIdx >= 0) acc.phase_steps[existingIdx] = step;
                else acc.phase_steps.push(step);
                updateThreadMessage(acc, { phase_steps: [...acc.phase_steps] });
              }
              break;
            }

            case 'action_preview': {
              updateThreadRef.current(msgThreadId, {
                status: 'approval_required',
                last_activity_at: new Date().toISOString(),
              });
              if (msg.data) {
                const serverPreviewId = msg.data.preview_id;
                // Deduplicate: if a card with this preview_id already exists, skip.
                // Prevents duplicate approval cards from race conditions or reconnects.
                const existingMessages = threadMessagesRef.current.get(msgThreadId) || [];
                if (existingMessages.some(m => m.action_preview?.preview_id === serverPreviewId)) {
                  console.log(`[useNChat] Duplicate action_preview ${serverPreviewId} — skipping`);
                  break;
                }

                const previewId = `preview-${Date.now()}`;
                setThreadMessages(msgThreadId, prev => [...prev, {
                  id: previewId,
                  role: 'assistant',
                  content: '',
                  timestamp: Date.now(),
                  action_preview: {
                    preview_id: msg.data.preview_id,
                    tool_name: msg.data.tool_name,
                    tool_args: msg.data.tool_args,
                    preview: msg.data.preview,
                    status: 'pending',
                  },
                }]);
              }
              break;
            }
            
            // ── Planning Mode events ──
            case 'planning_questions': {
              const acc = getAssistant();
              const session: PlanningSession = acc.planning_session || {
                status: 'questions',
                advisor_messages: [],
              };
              session.status = 'questions';
              session.questions = msg.data?.questions || [];
              acc.planning_session = session;
              updateThreadMessage(acc, { planning_session: { ...session } });
              break;
            }

            case 'planning_status': {
              const acc = getAssistant();
              const session: PlanningSession = acc.planning_session || {
                status: 'investigating',
                advisor_messages: [],
              };
              session.status = msg.data?.status || session.status;
              if (msg.data?.context_summary) session.context_summary = msg.data.context_summary;
              acc.planning_session = session;
              updateThreadMessage(acc, { planning_session: { ...session } });
              break;
            }

            case 'planning_advisor': {
              const acc = getAssistant();
              const session: PlanningSession = acc.planning_session || {
                status: 'debating',
                advisor_messages: [],
              };
              if (msg.data) {
                const existing = session.advisor_messages.find(
                  m => m.role === msg.data.role && m.round === msg.data.round
                );
                if (existing) {
                  existing.content = msg.data.content || existing.content;
                  if (msg.data.tool_calls) existing.tool_calls = msg.data.tool_calls;
                } else {
                  session.advisor_messages.push({
                    role: msg.data.role,
                    content: msg.data.content || '',
                    round: msg.data.round || 1,
                    tool_calls: msg.data.tool_calls,
                  });
                }
              }
              acc.planning_session = session;
              updateThreadMessage(acc, { planning_session: { ...session, advisor_messages: [...session.advisor_messages] } });
              break;
            }

            case 'planning_plan': {
              const acc = getAssistant();
              const session: PlanningSession = acc.planning_session || {
                status: 'complete',
                advisor_messages: [],
              };
              session.status = 'complete';
              if (msg.data?.plan) session.plan = msg.data.plan;
              acc.planning_session = session;
              updateThreadMessage(acc, { planning_session: { ...session } });
              break;
            }

            // ── Mar 2026: Live advisor activity feed ──
            case 'planning_advisor_activity': {
              const acc = getAssistant();
              const session: PlanningSession = acc.planning_session || {
                status: 'investigating',
                advisor_messages: [],
              };
              if (!session.advisor_activity) session.advisor_activity = [];
              session.advisor_activity.push({
                role: msg.data?.role || 'unknown',
                activity: msg.data?.activity || 'tool_call',
                tool_name: msg.data?.tool_name || '',
                summary: msg.data?.summary,
                iteration: msg.data?.iteration,
                timestamp: Date.now(),
              });
              // Keep last 20 activities to avoid unbounded growth
              if (session.advisor_activity.length > 20) {
                session.advisor_activity = session.advisor_activity.slice(-20);
              }
              acc.planning_session = session;
              updateThreadMessage(acc, { planning_session: { ...session, advisor_activity: [...session.advisor_activity] } });
              break;
            }

            case 'planning_advisor_phase': {
              const acc = getAssistant();
              const session: PlanningSession = acc.planning_session || {
                status: 'investigating',
                advisor_messages: [],
              };
              session.current_advisor = {
                role: msg.data?.role || 'unknown',
                phase: msg.data?.phase || 'started',
                advisor_index: msg.data?.advisor_index || 1,
                total_advisors: msg.data?.total_advisors || 3,
              };
              acc.planning_session = session;
              updateThreadMessage(acc, { planning_session: { ...session } });
              break;
            }

            case 'planning_checkpoint': {
              // Informational — backend will persist the full session on completion.
              break;
            }

            // Mar 2026: Thread summary received — create new tab with context
            case 'thread_summary': {
              // This is handled by the startFreshTab flow — see below
              break;
            }

            case 'system_notice': {
              // System notifications (credit fallback, etc.)
              if (msg.data?.type === 'credit_fallback') {
                import('sonner').then(({ toast }) => {
                  toast.warning(msg.data.message || 'Switched to free model due to insufficient credits', {
                    duration: 8000,
                  });
                });
              }
              break;
            }

            case 'done': {
              const acc = assistantsByThread.current.get(msgThreadId);
              const finalText = acc?.content?.slice(0, 140) || '';
              assistantsByThread.current.delete(msgThreadId);
              if (msgThreadId === activeThreadIdRef.current) currentAssistantRef.current = null;
              setStreamingThreads(prev => ({ ...prev, [msgThreadId]: false }));
              updateThreadRef.current(msgThreadId, {
                status: 'completed',
                last_activity_at: new Date().toISOString(),
                last_message_preview: finalText,
              });

              // FEB 2026: Auto-send queued/forced message now that N is done.
              // Two paths: forced (from forceInterrupt, UI already transitioned) or
              // queued (from client-side queue, needs UI transition).
              const forced = forcedMessageRef.current;
              if (forced && forced.threadId === msgThreadId && socket.readyState === WebSocket.OPEN) {
                // FORCED PATH — user pressed Enter to interrupt.
                // UI already shows the user bubble + thinking indicator (optimistic).
                // Just send to backend immediately, no delay.
                forcedMessageRef.current = null;
                setStreamingThreads(prev => ({ ...prev, [msgThreadId]: true }));
                socket.send(JSON.stringify(buildChatPayload(
                  msgThreadId,
                  forced.queued.text,
                  forced.queued.imageUrls || [],
                  threadModesRef.current[msgThreadId] || DEFAULT_THREAD_MODE_STATE,
                  undefined,
                  surfaceRef.current,
                )));
              } else {
                // NATURAL PATH — N finished on its own, queued message waiting.
                const queued = queuedMessagesRef.current[msgThreadId];
                if (queued && socket.readyState === WebSocket.OPEN) {
                  setQueuedMessages(prev => {
                    const next = { ...prev };
                    delete next[msgThreadId];
                    return next;
                  });

                  setTimeout(() => {
                    const userMsg: NChatMessage = {
                      id: `u-${Date.now()}`,
                      role: 'user',
                      content: queued.text,
                      timestamp: Date.now(),
                      attachments: queued.attachments,
                    };
                    setThreadMessages(msgThreadId, prev => [...prev, userMsg]);
                    setStreamingThreads(prev => ({ ...prev, [msgThreadId]: true }));
                    thinkingStartRef.current[msgThreadId] = Date.now();
                    setThinkingThreads(prev => ({ ...prev, [msgThreadId]: true }));

                    socket.send(JSON.stringify(buildChatPayload(
                      msgThreadId,
                      queued.text,
                      queued.imageUrls || [],
                      threadModesRef.current[msgThreadId] || DEFAULT_THREAD_MODE_STATE,
                      undefined,
                      surfaceRef.current,
                    )));
                  }, 100);
                }
              }
              break;
            }
            
            case 'error':
              console.error('[useNChat] Server error:', msg.data?.message);
              assistantsByThread.current.delete(msgThreadId);
              if (msgThreadId === activeThreadIdRef.current) currentAssistantRef.current = null;
              setStreamingThreads(prev => ({ ...prev, [msgThreadId]: false }));
              updateThreadRef.current(msgThreadId, {
                status: 'error',
                last_activity_at: new Date().toISOString(),
              });
              break;
          }
        } catch (err) {
          console.error('[useNChat] Message parse error:', err);
        }
      };

      socket.onerror = () => {
        console.error('[useNChat] WebSocket error');
        connectingRef.current = false; connectingForWorkspaceRef.current = null;
        setIsConnected(false);
      };

      socket.onclose = (event) => {
        console.log('[useNChat] WebSocket closed, code:', event.code);
        connectingRef.current = false; connectingForWorkspaceRef.current = null;
        setIsConnected(false);
        socketRef.current = null;
        
        // Auto-reconnect after 5 seconds (unless cleanly closed)
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[useNChat] Attempting reconnect...');
            connect();
          }, 5000);
        }
      };
    } catch (err) {
      console.error('[useNChat] Connection error:', err);
      connectingRef.current = false; connectingForWorkspaceRef.current = null;
      setIsConnected(false);
    }
  // Multiplexed: connect once per workspace, not per thread/session.
  // Thread routing happens via thread_id in each message.
  // CRITICAL: Do NOT include reloadMessagesFromDB or updateThread here.
  // They change identity on every thread switch, which would tear down
  // the socket and kill all in-flight agent runs. Use refs instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, user?.id, workerUrl]);

  // Upload a single blob/file to Supabase Storage, returns public URL
  const uploadToStorage = async (blob: Blob, fileName: string, contentType: string): Promise<string | null> => {
    const ext = fileName.split('.').pop() || 'bin';
    const path = `${currentWorkspace?.id}/${crypto.randomUUID()}.${ext}`;
    
    const { data, error } = await supabase.storage
      .from('chat-images')
      .upload(path, blob, { contentType });
    
    if (error) {
      console.error('[useNChat] Upload failed:', error);
      return null;
    }
    
    const { data: urlData } = supabase.storage
      .from('chat-images')
      .getPublicUrl(data.path);
    
    return urlData.publicUrl;
  };

  // Process files: extract text + screenshots, upload visual content
  // Returns { attachments (for display), imageUrls (for LLM), documentContext (text for LLM) }
  const processAndUploadFiles = async (files: File[]): Promise<{
    attachments: ChatAttachment[];
    imageUrls: string[];
    documentContext: string;
  }> => {
    const { processFile, isSupportedFileType } = await import('@/lib/file-to-screenshots');
    const attachments: ChatAttachment[] = [];
    const imageUrls: string[] = [];
    let documentContext = '';

    for (const file of files) {
      // Raw image files — upload directly (fast path, no processing needed)
      if (file.type.startsWith('image/')) {
        const url = await uploadToStorage(file, file.name, file.type);
        if (url) {
          attachments.push({ url, mime_type: file.type, filename: file.name });
          imageUrls.push(url);
        }
        continue;
      }

      // Document files (PDF, DOCX, XLSX, TXT) — process for text + screenshots
      if (!isSupportedFileType(file)) {
        console.warn(`[useNChat] Unsupported file type: ${file.type} (${file.name})`);
        continue;
      }

      try {
        const result = await processFile(file);
        console.log(`[useNChat] Processed ${file.name}: kind=${result.kind}, screenshots=${result.screenshots.length}, text=${result.textContent.length} chars, truncated=${result.truncated}`);

        // Upload screenshots (PDF page renders) as images for the LLM to see
        if (result.screenshots.length > 0) {
          for (const ss of result.screenshots) {
            // Convert data URL to blob for upload
            const res = await fetch(ss.dataUrl);
            const blob = await res.blob();
            const ssName = `${file.name}_page${ss.page}.jpg`;
            const url = await uploadToStorage(blob, ssName, 'image/jpeg');
            if (url) imageUrls.push(url);
          }
        }

        // Collect text content for the LLM
        if (result.textContent) {
          const truncationWarning = result.truncated
            ? `\n⚠️ WARNING: This file was too large to fully process. The content below is INCOMPLETE — some sheets or rows were cut off. Do NOT assume you can see the entire file. Tell the user that the file was truncated and your analysis may be partial.\n`
            : '';
          documentContext += `\n[Attached Document: ${file.name} (${result.pageCount} page${result.pageCount !== 1 ? 's' : ''})]${truncationWarning}\n${result.textContent}\n`;
        }

        // Add attachment chip for display in chat bubble
        // For visual files (PDFs), show a thumbnail of the first page
        const thumbnailUrl = result.screenshots[0]
          ? (() => { const r = fetch(result.screenshots[0].dataUrl).then(r => r.blob()); return undefined; })()
          : undefined;

        attachments.push({
          url: imageUrls[imageUrls.length - 1] || '',
          mime_type: file.type || 'application/octet-stream',
          filename: file.name,
        });
      } catch (err) {
        console.error(`[useNChat] File processing failed for ${file.name}:`, err);
      }
    }

    // Persist uploaded bytes — chat-first curation: a file dropped in chat ALWAYS
    // saves to the brain (persistent KG via /context/upload), including the sovereign /
    // Ministry edition. (Previously the Ministry edition routed to an ephemeral thread
    // bucket that deleted with the chat.) Requires a Bearer token.
    const workerUrl = import.meta.env.VITE_WORKER_URL || "https://n71-mission-worker.fly.dev";

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken || !currentWorkspace?.id) {
      if (files.length > 0) {
        console.warn("[useNChat] Skipping persistence — no session token or workspace");
      }
      return { attachments, imageUrls, documentContext: documentContext.trim() };
    }

    const persistResults = await Promise.allSettled(files.map(async (file) => {
      const buffer = await file.arrayBuffer();
      const base64 = btoa(new Uint8Array(buffer).reduce((d, b) => d + String.fromCharCode(b), ""));

      const endpoint = "/context/upload";
      const body = {
        workspace_id: currentWorkspace.id,
        user_id: user?.id,
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        content: base64,
        context: "Uploaded via Nchat conversation",
        source: "nchat",
      };

      const res = await fetch(`${workerUrl}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(`${file.name}: HTTP ${res.status}${detail ? ` ${detail.slice(0, 80)}` : ""}`);
      }
      return file.name;
    }));

    const failed = persistResults
      .map((r, i) => (r.status === "rejected" ? { name: files[i].name, reason: r.reason } : null))
      .filter((x): x is { name: string; reason: unknown } => x !== null);

    if (failed.length > 0) {
      const reasonText = failed[0].reason instanceof Error ? failed[0].reason.message : String(failed[0].reason);
      console.error("[useNChat] File persistence failed:", failed);
      try {
        const { toast } = await import("sonner");
        toast.error(
          failed.length === 1
            ? `Couldn't save ${failed[0].name}: ${reasonText}`
            : `${failed.length} of ${files.length} attachments failed to save`
        );
      } catch { /* toast optional */ }
    }

    // Chat-first curation: confirm the save so the user knows it's in the brain.
    const savedCount = persistResults.filter((r) => r.status === "fulfilled").length;
    if (savedCount > 0) {
      try {
        const { toast } = await import("sonner");
        toast.success(
          savedCount === 1
            ? `Saved to your brain — ask me about ${files[0]?.name ?? "it"} anytime.`
            : `Saved ${savedCount} files to your brain.`
        );
      } catch { /* toast optional */ }
    }

    return { attachments, imageUrls, documentContext: documentContext.trim() };
  };

  // Send message (with optional file attachments — images, PDFs, DOCX, XLSX, TXT)
  // Guard: prevent double-send from rapid Enter key / double-click
  const lastSentRef = useRef<{ content: string; ts: number }>({ content: '', ts: 0 });

  const sendMessage = useCallback(async (message: string, files?: File[], modelId?: string): Promise<SendMessageResult> => {
    // Debounce: reject identical messages within 2 seconds
    const now = Date.now();
    if (message === lastSentRef.current.content && now - lastSentRef.current.ts < 2000) {
      console.log('[useNChat] Duplicate message suppressed (debounce)');
      return { accepted: false, mode: 'suppressed', reason: 'duplicate_within_debounce_window' };
    }
    lastSentRef.current = { content: message, ts: now };

    let attachments: ChatAttachment[] | undefined;
    let imageUrls: string[] = [];
    let documentContext = '';

    // Process and upload files if provided
    if (files && files.length > 0) {
      setIsUploading(true);
      try {
        const result = await processAndUploadFiles(files);
        attachments = result.attachments.length > 0 ? result.attachments : undefined;
        imageUrls = result.imageUrls;
        documentContext = result.documentContext;
      } catch (err) {
        console.error('[useNChat] File processing error:', err);
      } finally {
        setIsUploading(false);
      }
    }

    // Build the message sent to the LLM — prepend document context if present
    const llmMessage = documentContext
      ? `${documentContext}\n\n${message}`
      : message;

    const threadId = activeThreadId || 'default';

    // ──────────────────────────────────────────────────────────────
    // FEB 2026: Cursor-style client-side queue
    // If N is already streaming on this thread, DON'T send to backend.
    // Hold the message client-side (shown as chip above input).
    // It auto-sends when the current task finishes ('done' event).
    // User can press Enter again (empty input) to force-interrupt.
    // ──────────────────────────────────────────────────────────────
    // Use a ref mirror to avoid stale closure races during rapid Enter presses.
    const isCurrentlyStreaming = streamingThreadsRef.current[threadId] || false;
    if (isCurrentlyStreaming) {
      console.log(`[useNChat] Thread ${threadId} is busy — holding message client-side`);
      setQueuedMessages(prev => ({
        ...prev,
        [threadId]: { text: llmMessage, imageUrls, attachments },
      }));
      return { accepted: true, mode: 'queued' }; // Don't add to chat, don't send to backend
    }

    // Normal flow: add user message to chat immediately (optimistic)
    const userMsg: NChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: Date.now(),
      attachments,
    };
    setThreadMessages(threadId, prev => [...prev, userMsg]);
    if (activeThreadId) {
      updateThread(activeThreadId, {
        status: 'running',
        last_activity_at: new Date().toISOString(),
        last_message_preview: message.slice(0, 140),
      });
      const activeThread = threadsRef.current.find(t => t.id === activeThreadId);
      const trimmed = message.trim();
      const shouldAutoTitle =
        !!activeThread &&
        isGenericThreadTitle(activeThread.title) &&
        trimmed.length > 0 &&
        trimmed.toLowerCase() !== 'brief me. what should i know right now?';
      if (shouldAutoTitle) {
        const autoTitle = trimmed.slice(0, 40);
        updateThread(activeThreadId, { title: autoTitle });
      }
    }

    // ──────────────────────────────────────────────────────────────
    // SOVEREIGN GROUNDED CHAT
    // When the workspace has strict_compliance_mode, skip the /n/stream
    // WebSocket entirely and POST this turn to the worker's
    // /n/conversational-stream endpoint. The worker persists both turns,
    // returns a grounded answer + citations (or a refusal), and we render
    // them directly. Non-sovereign behavior (below) is untouched.
    // ──────────────────────────────────────────────────────────────
    if (groundedChat && currentWorkspace?.id) {
      setStreamingThreads(prev => ({ ...prev, [threadId]: true }));
      thinkingStartRef.current[threadId] = Date.now();
      setThinkingThreads(prev => ({ ...prev, [threadId]: true }));
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;
        if (!accessToken) throw new Error('No session token');

        const res = await fetch(`${workerUrl}/n/conversational-stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            workspace_id: currentWorkspace.id,
            thread_id: activeThreadId || undefined,
            query: llmMessage,
            model: modelId,
          }),
        });
        if (!res.ok) {
          const detail = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}${detail ? ` ${detail.slice(0, 120)}` : ''}`);
        }
        const data = await res.json() as {
          answer: string;
          citations?: ChatCitation[];
          refused?: boolean;
          message_id?: string | null;
        };

        const assistantMsg: NChatMessage = {
          id: data.message_id || `n-${Date.now()}-${threadId.slice(0, 8)}`,
          role: 'assistant',
          content: data.answer || '',
          timestamp: Date.now(),
          citations: data.citations && data.citations.length > 0 ? data.citations : undefined,
          grounded_refused: !!data.refused,
        };
        setThreadMessages(threadId, prev => [...prev, assistantMsg]);
        if (activeThreadId) {
          updateThread(activeThreadId, {
            status: 'completed',
            last_activity_at: new Date().toISOString(),
            last_message_preview: (data.answer || '').slice(0, 140),
          });
        }
        return { accepted: true, mode: 'sent' };
      } catch (err) {
        console.error('[useNChat] Grounded chat request failed:', err);
        setThreadMessages(threadId, prev => [...prev, {
          id: `n-err-${Date.now()}`,
          role: 'assistant',
          content: 'I could not reach the grounded answer service. Please try again.',
          timestamp: Date.now(),
          grounded_refused: true,
        }]);
        if (activeThreadId) {
          updateThread(activeThreadId, { status: 'error', last_activity_at: new Date().toISOString() });
        }
        return { accepted: false, mode: 'suppressed', reason: 'grounded_request_failed' };
      } finally {
        setStreamingThreads(prev => ({ ...prev, [threadId]: false }));
        setThinkingThreads(prev => ({ ...prev, [threadId]: false }));
      }
    }

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.log('[useNChat] Socket not open, queuing message and reconnecting...');
      pendingMessagesRef.current.push({
        message: llmMessage,
        threadId,
        chatMode: activeModes.chatMode,
        deepResearchMode: activeModes.deepResearchMode,
        imageUrls,
      });
      connect();
      return { accepted: true, mode: 'deferred' };
    }

    setStreamingThreads(prev => ({ ...prev, [threadId]: true }));
    thinkingStartRef.current[threadId] = Date.now();
    setThinkingThreads(prev => ({ ...prev, [threadId]: true }));
    currentAssistantRef.current = null;

    const wsPayload: Record<string, any> = buildChatPayload(
      threadId,
      llmMessage,
      imageUrls,
      activeModes,
      modelId,
      surfaceRef.current,
    );

    // Attach referenced context so backend builds a routing prefix.
    // Read from ref (not closure) — referencedContext state is NOT in useCallback deps,
    // so the closure value is stale. The ref always reflects the latest button click.
    const pinnedRef = referencedContextRef.current;
    if (pinnedRef) {
      wsPayload.referenced_mission_id = pinnedRef.mission_id;
      wsPayload.referenced_mission_name = pinnedRef.mission_name;
      wsPayload.referenced_intent = pinnedRef.intent;
      if (pinnedRef.artifact_id) {
        wsPayload.referenced_artifact_id = pinnedRef.artifact_id;
        wsPayload.referenced_artifact_type = pinnedRef.artifact_type;
        wsPayload.referenced_artifact_name = pinnedRef.artifact_name;
      }
      // Clear after sending so next message starts fresh
      setReferencedContext(null);
    }

    socketRef.current.send(JSON.stringify(wsPayload));
    return { accepted: true, mode: 'sent' };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect, currentWorkspace?.id, activeModes.chatMode, activeModes.deepResearchMode, activeThreadId, updateThread, groundedChat]);

  const sendActionResponse = useCallback((previewId: string, approved: boolean, agent?: string) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[useNChat] Socket not connected, cannot send action response');
      return;
    }

    const threadId = activeThreadIdRef.current || 'default';
    socketRef.current.send(JSON.stringify({
      type: 'action_response',
      thread_id: threadId,
      preview_id: previewId,
      approved,
      // Optional agent override from the dispatch_task "run with" picker.
      ...(agent ? { agent } : {}),
    }));

    // Update approval status in both React state AND per-thread ref (Mar 2026: use 'skipped' instead of 'rejected')
    const patchApproval = (msgs: NChatMessage[]) => msgs.map(msg => {
      if (msg.action_preview?.preview_id === previewId) {
        return {
          ...msg,
          action_preview: {
            ...msg.action_preview,
            status: (approved ? 'approved' : 'skipped') as 'approved' | 'skipped',
            actioned_at: new Date().toISOString(),
          },
        };
      }
      return msg;
    });

    setMessages(prev => patchApproval(prev));
    // Also update the per-thread ref so tab switches don't revert the status
    const threadMsgs = threadMessagesRef.current.get(threadId);
    if (threadMsgs) {
      threadMessagesRef.current.set(threadId, patchApproval(threadMsgs));
    }
  }, []);

  // ============================================
  // FORCE INTERRUPT — Cursor-style: user presses Enter on empty input while
  // a message is queued. Optimistic UI: chip becomes user bubble + thinking
  // indicator instantly. Backend send happens when 'done' arrives.
  // ============================================
  const forceInterrupt = useCallback(() => {
    const threadId = activeThreadIdRef.current;
    if (!threadId) return;

    if (!streamingThreads[threadId]) return;
    const queued = queuedMessagesRef.current[threadId];
    if (!queued) return;

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[useNChat] Socket not connected, cannot send interrupt');
      return;
    }

    console.log(`[useNChat] Force interrupt for thread ${threadId}`);

    // ── OPTIMISTIC UI: instant visual transition ──
    // 1. Clear chip immediately
    setQueuedMessages(prev => {
      const next = { ...prev };
      delete next[threadId];
      return next;
    });

    // 2. Add queued message to chat as user bubble immediately (no waiting for 'done')
    const userMsg: NChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: queued.text,
      timestamp: Date.now(),
      attachments: queued.attachments,
    };
    setThreadMessages(threadId, prev => [...prev, userMsg]);

    // 3. Clean up current assistant accumulator (the interrupted response)
    assistantsByThread.current.delete(threadId);
    if (threadId === activeThreadIdRef.current) {
      currentAssistantRef.current = null;
    }

    // 4. Show thinking indicator for the new message immediately
    thinkingStartRef.current[threadId] = Date.now();
    setThinkingThreads(prev => ({ ...prev, [threadId]: true }));

    // 5. Store in a ref so the 'done' handler sends it to backend (not queuedMessages)
    forcedMessageRef.current = { threadId, queued };

    // 6. Send stop — backend will abort, send 'done', done handler sends the forced message
    socketRef.current.send(JSON.stringify({
      type: 'stop',
      thread_id: threadId,
    }));
  }, [streamingThreads]);

  // ============================================
  // STOP GENERATION — sends stop signal for the active thread
  // Tied to the current thread_id so it only stops the tab the user is looking at.
  // ============================================
  const stopGeneration = useCallback(() => {
    const threadId = activeThreadIdRef.current;
    if (!threadId) return;

    // Only send if we believe this thread is streaming
    if (!streamingThreads[threadId]) return;

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[useNChat] Socket not connected, cannot send stop');
      return;
    }

    console.log(`[useNChat] Stopping generation for thread ${threadId}`);
    socketRef.current.send(JSON.stringify({
      type: 'stop',
      thread_id: threadId,
    }));

    // Optimistically clear streaming state — backend will send 'done' to confirm
    setStreamingThreads(prev => ({ ...prev, [threadId]: false }));

    // Clean up the assistant accumulator for this thread
    assistantsByThread.current.delete(threadId);
    if (threadId === activeThreadIdRef.current) {
      currentAssistantRef.current = null;
    }
  }, [streamingThreads]);

  // ============================================
  // EXECUTE PLAN — sends structured plan JSON to PlanExecutor for deterministic execution.
  // 
  // FEB 2026 REWRITE: Previously sent the plan as a text blob to N Agent,
  // which paraphrased/lost detail. Now sends type: "execute_plan" with the
  // structured plan JSON. The backend PlanExecutor iterates steps deterministically,
  // passing each step's FULL description to the correct tier executor.
  // No LLM re-interpretation. The plan IS the execution script.
  // ============================================
  const executePlan = useCallback((planSession: PlanningSession) => {
    if (!planSession.plan) {
      console.warn('[useNChat] executePlan called without a plan');
      return;
    }

    const plan = planSession.plan;
    const threadId = activeThreadIdRef.current || 'default';

    // Short display message shown in the chat UI
    const displayMessage = `Execute plan: ${plan.objective}`;

    // Mar 2026: Optimistically set execution state on the planning session message
    // so the card immediately transitions to "executing" view with pending step statuses.
    setThreadMessages(threadId, prev => prev.map(m => {
      if (m.planning_session?.plan && m.planning_session.status === 'complete') {
        return {
          ...m,
          planning_session: {
            ...m.planning_session,
            execution: {
              status: 'running' as const,
              step_statuses: plan.steps.map((_, i) => ({
                index: i + 1,
                status: 'pending' as const,
              })),
            },
          },
        };
      }
      return m;
    }));

    // Optimistic user message in the UI
    const userMsg: NChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: displayMessage,
      timestamp: Date.now(),
    };
    setThreadMessages(threadId, prev => [...prev, userMsg]);

    if (activeThreadIdRef.current) {
      updateThread(activeThreadIdRef.current, {
        status: 'running',
        last_activity_at: new Date().toISOString(),
        last_message_preview: displayMessage.slice(0, 140),
      });
    }

    // Send structured plan to backend — PlanExecutor handles routing
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.log('[useNChat] Socket not open, queuing plan execution and reconnecting...');
      pendingMessagesRef.current.push({
        message: displayMessage,
        threadId,
        chatMode: 'normal',
        deepResearchMode: false,
        imageUrls: [],
        // Store plan for re-send after reconnect
        _plan: plan,
        _isPlanExecution: true,
      });
      connect();
      return;
    }

    setStreamingThreads(prev => ({ ...prev, [threadId]: true }));
    currentAssistantRef.current = null;

    // Send as structured execute_plan message — NOT a chat text blob
    socketRef.current.send(JSON.stringify({
      type: 'execute_plan',         // ← PlanExecutor route, not N Agent
      thread_id: threadId,
      plan: plan,                   // ← Full structured plan JSON
      message: displayMessage,      // ← For display/logging
    }));

    // Switch thread to normal mode so subsequent messages aren't in plan mode
    setThreadModes(prev => ({
      ...prev,
      [threadId]: { chatMode: 'normal', deepResearchMode: prev[threadId]?.deepResearchMode ?? false },
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect, updateThread]);

  // ============================================
  // SUBMIT PLAN ANSWERS — sends clicked option answers to the backend
  // so the planning pipeline can use them as additional context.
  // The user can also just type in the chat (sendMessage handles that path
  // automatically since the backend detects pendingPlanRequest).
  // ============================================
  const submitPlanAnswers = useCallback((answers: Array<{ question: string; answer: string }>) => {
    const threadId = activeThreadIdRef.current || 'default';

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[useNChat] Cannot submit plan answers — socket not open');
      return;
    }

    // Show selected answers as a compact user message in chat
    const displayText = answers.map(a => a.answer).join(' · ');
    const userMsg: NChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: displayText,
      timestamp: Date.now(),
    };
    setThreadMessages(threadId, prev => [...prev, userMsg]);

    if (activeThreadIdRef.current) {
      updateThread(activeThreadIdRef.current, {
        status: 'running',
        last_activity_at: new Date().toISOString(),
        last_message_preview: displayText.slice(0, 140),
      });
    }

    setStreamingThreads(prev => ({ ...prev, [threadId]: true }));
    currentAssistantRef.current = null;

    socketRef.current.send(JSON.stringify({
      type: 'chat',
      thread_id: threadId,
      message: displayText,
      planning_mode: true,
      plan_answers: answers,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateThread]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    currentAssistantRef.current = null;
  }, []);

  const createThread = useCallback(async () => {
    if (!currentWorkspace?.id || !user?.id) return;
    const now = new Date().toISOString();
    // Use a callback to read current thread count without depending on threads.length
    // This keeps createThread's identity stable so the init effect doesn't re-run.
    let nextSortOrder = 0;
    setThreads(prev => { nextSortOrder = prev.length; return prev; });
    const { data: createdThread, error: threadErr } = await supabase
      .from('n_chat_threads')
      .insert({
        workspace_id: currentWorkspace.id,
        owner_user_id: user.id,
        scope: 'global',
        title: 'New chat',
        status: 'idle',
        last_activity_at: now,
        sort_order: nextSortOrder,
      })
      .select('id, title, status, last_activity_at, last_message_preview')
      .single();
    if (threadErr || !createdThread?.id) {
      console.error('[useNChat] Failed to create thread:', threadErr?.message);
      return;
    }
    const newThread: NChatThread = {
      id: createdThread.id,
      title: createdThread.title || 'New chat',
      status: (createdThread.status || 'idle') as NChatThread['status'],
      last_activity_at: createdThread.last_activity_at || now,
      last_message_preview: createdThread.last_message_preview || '',
      unread_count: 0,
    };
    setThreads(prev => [...prev, newThread]);
    const sessionId = await ensureSessionForThread(currentWorkspace.id, user.id, createdThread.id);
    threadMessagesRef.current.set(createdThread.id, []);
    setThreadModes(prev => ({
      ...prev,
      [createdThread.id]: { ...DEFAULT_THREAD_MODE_STATE },
    }));
    setActiveThreadId(createdThread.id);
    setActiveSessionId(sessionId);
    setMessages([]);
    currentAssistantRef.current = null;
  }, [currentWorkspace?.id, user?.id]);

  // Mar 2026: Start a fresh tab with summarized context from the current conversation.
  // 1. Sends summarize_thread to backend → gets a summary
  // 2. Creates a new tab
  // 3. Sends the summary as the first message in the new tab
  const startFreshTab = useCallback(() => {
    const threadId = activeThreadIdRef.current;
    if (!threadId || !socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;

    setIsSummarizing(true);

    // Set up a one-time listener for the summary response
    const originalOnMessage = socketRef.current.onmessage;
    const summaryHandler = async (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'thread_summary') {
          // Restore original handler
          if (socketRef.current) socketRef.current.onmessage = originalOnMessage;
          setIsSummarizing(false);

          const summary = msg.data?.summary || '';

          // Create new tab
          await createThread();

          // Wait a beat for the new tab to be set up, then send the context
          setTimeout(() => {
            const newThreadId = activeThreadIdRef.current;
            if (summary && newThreadId && socketRef.current?.readyState === WebSocket.OPEN) {
              // Send the summary as the first message to establish context
              const contextMessage = `[Context from previous conversation]\n\n${summary}\n\nI've started a fresh tab with context from our previous conversation. How can I help?`;
              socketRef.current.send(JSON.stringify(buildChatPayload(
                newThreadId,
                contextMessage,
                [],
                threadModesRef.current[newThreadId] || DEFAULT_THREAD_MODE_STATE,
                undefined,
                surfaceRef.current,
              )));

              // Add system message to UI
              setThreadMessages(newThreadId, prev => [...prev, {
                id: `ctx-${Date.now()}`,
                role: 'assistant',
                content: `I've carried over context from your previous conversation:\n\n${summary}\n\nWhat would you like to do next?`,
                timestamp: Date.now(),
              }]);
            }
          }, 500);
          return;
        }
      } catch { /* ignore parse errors */ }

      // For non-summary messages, call the original handler
      if (originalOnMessage) originalOnMessage.call(socketRef.current!, event);
    };

    // Temporarily intercept messages to catch the summary
    socketRef.current.onmessage = summaryHandler;

    // Request summary from backend
    socketRef.current.send(JSON.stringify({
      type: 'summarize_thread',
      thread_id: threadId,
    }));

    // Timeout — if no response in 15 seconds, give up
    setTimeout(() => {
      if (isSummarizing) {
        setIsSummarizing(false);
        if (socketRef.current) socketRef.current.onmessage = originalOnMessage;
        console.warn('[useNChat] Thread summarization timed out');
      }
    }, 15000);
  }, [createThread]);

  const selectThread = useCallback(async (threadId: string) => {
    if (!currentWorkspace?.id || !user?.id) return;
    if (threadId === activeThreadId) return;

    // Save current thread's messages before switching
    if (activeThreadId) {
      // Messages are already in threadMessagesRef via setThreadMessages — no extra save needed
    }

    const sessionId = await ensureSessionForThread(currentWorkspace.id, user.id, threadId);
    setActiveThreadId(threadId);
    setActiveSessionId(sessionId);

    // Restore the new thread's messages from the store
    const stored = threadMessagesRef.current.get(threadId);
    setMessages(stored || []);

    // Point legacy ref to the new thread's accumulator (if streaming)
    currentAssistantRef.current = assistantsByThread.current.get(threadId) || null;
    // Do NOT close the socket — multiplexed connection stays open
  }, [currentWorkspace?.id, user?.id, activeThreadId]);

  const renameThread = useCallback(async (threadId: string, title: string) => {
    const cleanTitle = title.trim().slice(0, 80);
    if (!cleanTitle || !currentWorkspace?.id || !user?.id) return;
    setThreads(prev => prev.map(t => t.id === threadId ? { ...t, title: cleanTitle } : t));
    await supabase
      .from('n_chat_threads')
      .update({ title: cleanTitle, updated_at: new Date().toISOString() })
      .eq('id', threadId)
      .eq('workspace_id', currentWorkspace.id)
      .eq('owner_user_id', user.id);
  }, [currentWorkspace?.id, user?.id]);

  const reorderThreads = useCallback(async (orderedThreadIds: string[]) => {
    if (!orderedThreadIds.length) return;
    setThreads(prev => {
      const map = new Map(prev.map(t => [t.id, t]));
      return orderedThreadIds.map(id => map.get(id)).filter(Boolean) as NChatThread[];
    });
    await persistThreadOrder(orderedThreadIds);
  }, [persistThreadOrder]);

  // Archive a specific thread (or current if no id given). Switches to next available thread.
  const archiveThread = useCallback(async (threadId?: string) => {
    const currentActiveId = activeThreadIdRef.current;
    const targetId = threadId || currentActiveId;
    if (!currentWorkspace?.id || !user?.id || !targetId) return;

    try {
      await supabase
        .from('n_chat_threads')
        .update({
          is_archived: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', targetId)
        .eq('workspace_id', currentWorkspace.id)
        .eq('owner_user_id', user.id);

      const remaining = threadsRef.current.filter(t => t.id !== targetId);
      setThreads(remaining);

      // If we archived the active tab, switch to the next one
      if (targetId === currentActiveId) {
        const next = remaining[0];
        if (next) {
          await selectThread(next.id);
        } else {
          await createThread();
        }
      }
    } catch (err) {
      console.error('[useNChat] Failed to archive chat:', err);
    }
  }, [currentWorkspace?.id, user?.id, selectThread, createThread]);

  // Convenience alias for archiving the active chat
  const archiveChat = useCallback(() => archiveThread(), [archiveThread]);

  // Initialize thread list + active thread/session.
  // CRITICAL: This must only run once per workspace/user login.
  // Do NOT include createThread in deps — it causes re-init on every tab creation
  // which clobbers thread names and resets the active tab.
  const createThreadRef = useRef(createThread);
  createThreadRef.current = createThread;

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!currentWorkspace?.id || !user?.id) return;

      // ── Workspace-switch cleanup ──
      // Clear ALL in-memory state from the previous workspace so nothing leaks across.
      // Without this, messages, mode toggles (plan/ask), streaming indicators, and
      // assistant accumulators from the old workspace persist until async loads finish.
      threadMessagesRef.current.clear();
      assistantsByThread.current.clear();
      currentAssistantRef.current = null;
      pendingMessagesRef.current = [];
      setMessages([]);
      setThreadModes({});
      setStreamingThreads({});
      setActiveSessionId(null);

      const loadedThreads = await loadThreadsFromDB(currentWorkspace.id, user.id);
      if (cancelled) return;

      if (loadedThreads.length > 0) {
        setThreads(loadedThreads);
        const initialThread = loadedThreads[0];
        setActiveThreadId(initialThread.id);
        const sid = await ensureSessionForThread(currentWorkspace.id, user.id, initialThread.id);
        if (!cancelled) setActiveSessionId(sid);

      } else {
        await createThreadRef.current();
      }
    };
    void init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, user?.id]);

  // Reload chat history whenever active session changes.
  useEffect(() => {
    if (!activeSessionId) return;
    reloadMessagesFromDB();
  }, [activeSessionId, reloadMessagesFromDB]);

  // Connect once per workspace — multiplexed socket stays open for all threads.
  // Also handles iOS Safari reconnection via visibilitychange and periodic health checks.
  useEffect(() => {
    // Strict-compliance workspaces are pure HTTP (/n/conversational-stream) —
    // never open the /n/stream WebSocket. Skip the connect + reconnect
    // machinery entirely so there's no orphan socket or reconnect loop.
    if (groundedChat) return;

    if (currentWorkspace?.id && user?.id) {
      connect();
    }

    // iOS Safari (and mobile browsers in general) aggressively kill WebSocket connections
    // when the tab or app goes to background. When the user returns, we need to reconnect.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const socket = socketRef.current;
        if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
          console.log('[useNChat] Tab became visible — socket dead, reconnecting...');
          socketRef.current = null;
          setIsConnected(false);
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic health check — detect silently-dead connections.
    // iOS can kill the TCP connection without firing onclose.
    const healthCheck = setInterval(() => {
      const socket = socketRef.current;
      if (!socket) return;

      if (socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
        console.log('[useNChat] Health check: socket dead, reconnecting...');
        socketRef.current = null;
        setIsConnected(false);
        connect();
      }
    }, 15_000); // Check every 15 seconds

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(healthCheck);
      if (socketRef.current) {
        socketRef.current.close(1000, 'Component unmounting');
        socketRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [currentWorkspace?.id, user?.id, connect, groundedChat]);

  // Subscribe to mission completions — inject results into chat
  useEffect(() => {
    if (!currentWorkspace?.id) return;

    // Track which mission IDs N launched (from chat messages)
    const launchedMissionIds = new Set(
      messages
        .filter(m => m.mission_launched?.mission_id)
        .map(m => m.mission_launched!.mission_id)
    );

    if (launchedMissionIds.size === 0) return;

    // Watch for mission status changes
    const channel = supabase
      .channel(`n_chat_missions:${currentWorkspace.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'missions',
          filter: `workspace_id=eq.${currentWorkspace.id}`,
        },
        async (payload) => {
          const mission = payload.new as any;
          if (!launchedMissionIds.has(mission.id)) return;
          
          const oldStatus = (payload.old as any)?.status;
          const newStatus = mission.status;
          
          // Only react to terminal transitions
          if (oldStatus === newStatus) return;
          
          if (newStatus === 'completed') {
            const completionMsg: NChatMessage = {
              id: `completion-${mission.id}`,
              role: 'assistant',
              content: `Done — "${mission.name}" completed.`,
              timestamp: Date.now(),
              mission_launched: {
                mission_id: mission.id,
                mission_name: mission.name,
              },
            };
            setMessages(prev => [...prev, completionMsg]);
          } else if (newStatus === 'failed' || newStatus === 'archived' || newStatus === 'paused') {
            // FIX: Include 'paused' — failures are now set to 'paused' (not 'archived')
            // so they stay visible on the grid. Catch all failure states here.
            const failMsg: NChatMessage = {
              id: `fail-${mission.id}`,
              role: 'assistant',
              content: `"${mission.name}" failed. Want me to check what went wrong?`,
              timestamp: Date.now(),
            };
            setMessages(prev => [...prev, failMsg]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentWorkspace?.id, messages]);

  // Surface setter — a mounted surface (Intelligence tab) calls this so the
  // next chat frame carries the correct marker. Stored in a ref so changing
  // it never re-renders or invalidates send callbacks.
  const setSurface = useCallback((s: string) => {
    surfaceRef.current = s;
  }, []);

  return {
    messages,
    threads,
    activeThreadId,
    chatMode: activeModes.chatMode,
    deepResearchMode: activeModes.deepResearchMode,
    setChatMode,
    setDeepResearchMode,
    // Grounded chat has no persistent socket — report "connected" so the
    // composer isn't disabled waiting on a WebSocket that never opens.
    isConnected: groundedChat ? true : isConnected,
    isStreaming,
    isThinking,
    isUploading,
    sendMessage,
    sendActionResponse,
    executePlan,
    submitPlanAnswers,
    stopGeneration,
    forceInterrupt,
    clearMessages,
    archiveChat,
    archiveThread,
    createThread,
    selectThread,
    renameThread,
    reorderThreads,
    referencedContext,
    setReferencedContext,
    queuedMessage,
    startFreshTab,
    isSummarizing,
    setSurface,
  };
}

export default useNChat;
